/**
 * Components Tab
 * Displays extracted component information
 */

import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import { Memory as ComponentIcon } from '@mui/icons-material';
import { Component } from '../../types/bom.types';

interface ComponentsTabProps {
  components: Component[];
}

const ComponentsTab: React.FC<ComponentsTabProps> = ({ components }) => {
  if (!components || components.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info">
          No component data available. Extract from a page first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ComponentIcon color="primary" />
        <Typography variant="h6">
          Component Analysis ({components.length} unique components)
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Component Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Count</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Examples</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pages</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {components.map((component, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {component.type || 'Unknown'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={component.count || 0} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(component.examples || []).slice(0, 5).map((example, idx) => (
                      <Chip
                        key={idx}
                        label={example}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                    {(component.examples || []).length > 5 && (
                      <Chip
                        label={`+${component.examples.length - 5} more`}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {(component.pages || []).join(', ')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ComponentsTab;

